import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchUserEducations } from "../../Models/firebaseHelper";
import { keys } from "../../strings";
import useLogin from "../../hooks/useLogin";
import getCourseProgress from "../../util/getCourseProgress";
import { useUser } from "../User/UserObject";
import type { Course, MenuEducation, MenuProps } from "./Menu.types";
import { matchCoursesToEducations } from "./LeftMenu.functions";
import { shouldShowEducationListSkeleton } from "./educationListLoading";

type UseMenuEducationsOptions = {
  availableCourses: MenuProps["availableCourses"];
  selectedCourse: Course | null;
  onSelectCourse: (course: Course) => void;
};

function toCatalogCourses(
  availableCourses: MenuProps["availableCourses"],
): Course[] {
  return availableCourses as Course[];
}

export function useMenuEducations({
  availableCourses,
  selectedCourse,
  onSelectCourse,
}: UseMenuEducationsOptions) {
  const { t } = useTranslation();
  const loggedIn = useLogin();
  const { user } = useUser();
  const [rawUserCourses, setRawUserCourses] = useState<Education[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCoursesError, setUserCoursesError] = useState<string | null>(null);

  const catalogCourses = useMemo(
    () => toCatalogCourses(availableCourses),
    [availableCourses],
  );

  const userEducations = useMemo(
    (): MenuEducation[] =>
      matchCoursesToEducations(rawUserCourses, catalogCourses),
    [rawUserCourses, catalogCourses],
  );

  const publishedEducations = useMemo(
    () =>
      userEducations.filter((education) => {
        if (
          education.PublishedStatus === undefined ||
          education.PublishedStatus === null
        ) {
          return true;
        }
        return (
          education.PublishedStatus === "published" ||
          education.PublishedStatus === "expired"
        );
      }),
    [userEducations],
  );

  const userOngoingEducations = useMemo(
    () =>
      publishedEducations.filter(
        (education) =>
          education.PublishedStatus !== "expired" &&
          getCourseProgress(education, user) < 100,
      ),
    [publishedEducations, user],
  );

  const userExpiredOngoingEducations = useMemo(
    () =>
      publishedEducations.filter(
        (education) =>
          education.PublishedStatus === "expired" &&
          getCourseProgress(education, user) < 100,
      ),
    [publishedEducations, user],
  );

  const userExpiredEducations = useMemo(
    () =>
      publishedEducations.filter(
        (education) =>
          education.PublishedStatus === "expired" &&
          getCourseProgress(education, user) === 100,
      ),
    [publishedEducations, user],
  );

  const userCompletedEducations = useMemo(
    () =>
      publishedEducations.filter(
        (education) =>
          education.PublishedStatus !== "expired" &&
          getCourseProgress(education, user) === 100,
      ),
    [publishedEducations, user],
  );

  const showEducationListSkeleton = shouldShowEducationListSkeleton({
    loading,
    hasUserCoursesError: Boolean(userCoursesError),
    loggedIn: Boolean(loggedIn),
    rawUserCoursesCount: rawUserCourses.length,
    availableCoursesCount: availableCourses.length,
  });

  const fetchUserCourses = useCallback(
    async (retryCount = 0, options?: { silent?: boolean }) => {
      const maxRetries = 3;
      const retryDelay = 1000 * Math.pow(2, retryCount);

      if (retryCount === 0 && !options?.silent) {
        setLoading(true);
      }
      setUserCoursesError(null);

      try {
        const userCourses = await fetchUserEducations();

        if (!userCourses) {
          if (retryCount < maxRetries) {
            setTimeout(() => {
              fetchUserCourses(retryCount + 1);
            }, retryDelay);
            return;
          }
          setRawUserCourses([]);
          setUserCoursesError(t("error.userCoursesLoadFailed"));
        } else {
          setRawUserCourses(userCourses);
        }
      } catch (error: unknown) {
        console.error(
          `Error fetching courses (attempt ${retryCount + 1}):`,
          error,
        );

        if (retryCount < maxRetries) {
          setTimeout(() => {
            fetchUserCourses(retryCount + 1);
          }, retryDelay);
          return;
        }
        setRawUserCourses([]);
        setUserCoursesError(
          error instanceof Error
            ? error.message
            : t("error.userCoursesLoadFailed"),
        );
      }

      setLoading(false);
    },
    [t],
  );

  const retryFetchUserCourses = useCallback(() => {
    fetchUserCourses(0);
  }, [fetchUserCourses]);

  useEffect(() => {
    if (loggedIn) {
      fetchUserCourses();
    } else {
      setRawUserCourses([]);
      setLoading(false);
    }
  }, [fetchUserCourses, loggedIn]);

  useEffect(() => {
    const handleRefreshCourses = (event: Event) => {
      if (!loggedIn) {
        return;
      }
      const silent =
        event instanceof CustomEvent && event.detail?.silent === true;
      fetchUserCourses(0, { silent });
    };

    window.addEventListener("refreshMenuCourses", handleRefreshCourses);
    return () =>
      window.removeEventListener("refreshMenuCourses", handleRefreshCourses);
  }, [loggedIn, fetchUserCourses]);

  useEffect(() => {
    const savedCourse = localStorage.getItem(keys.localStorage.selectedCourse);
    const totalCourses =
      userOngoingEducations.length +
      userExpiredOngoingEducations.length +
      userExpiredEducations.length +
      userCompletedEducations.length;
    if (!selectedCourse && savedCourse && totalCourses > 0) {
      try {
        const parsedCourse = JSON.parse(savedCourse) as Course;
        const allCourses = [
          ...userOngoingEducations,
          ...userExpiredOngoingEducations,
          ...userExpiredEducations,
          ...userCompletedEducations,
        ];
        const validCourse = allCourses.find((c) => c.ID === parsedCourse.ID);
        if (validCourse) {
          onSelectCourse(validCourse);
        }
      } catch {
        localStorage.removeItem(keys.localStorage.selectedCourse);
      }
    }
  }, [
    userOngoingEducations,
    userExpiredOngoingEducations,
    userExpiredEducations,
    userCompletedEducations,
    selectedCourse,
    onSelectCourse,
  ]);

  return {
    userCoursesError,
    retryFetchUserCourses,
    showEducationListSkeleton,
    userOngoingEducations,
    userExpiredOngoingEducations,
    userExpiredEducations,
    userCompletedEducations,
  };
}
